import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Briefcase, Clock, Users, DollarSign, FileText,
  ChevronLeft, ChevronRight, ChevronDown, Search,
  Check, Loader2, ExternalLink, User
} from 'lucide-react';
import { searchMatters } from '../../services/mailService';
import { linkEmailToCase } from '../../services/mailService';
import mailApi from '../../services/mailApi';

// ============================================================================
// Helpers
// ============================================================================

const SIDEBAR_KEY = 'virgomail_sidebar_expanded';
const panelKey = (name) => `virgomail_panel_${name}_expanded`;

const getStored = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : v === 'true';
  } catch { return fallback; }
};

const setStored = (key, val) => {
  try { localStorage.setItem(key, String(val)); } catch {}
};

// ============================================================================
// Collapsible panel wrapper
// ============================================================================

const Panel = ({ name, icon: Icon, title, children, defaultOpen = true, count }) => {
  const [open, setOpen] = useState(() => getStored(panelKey(name), defaultOpen));

  const toggle = () => {
    const next = !open;
    setOpen(next);
    setStored(panelKey(name), next);
  };

  return (
    <div className="border-b border-slate-700/50">
      <button onClick={toggle}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-200 transition-colors">
        <Icon className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="flex-1 text-left">{title}</span>
        {count !== undefined && count > 0 && (
          <span className="px-1.5 py-0.5 bg-cyan-600/30 text-cyan-300 rounded text-[10px] font-bold">{count}</span>
        )}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
};

// ============================================================================
// Panel 1: Matter
// ============================================================================

const MatterPanel = ({ intelligence, emailId, onMatterLinked }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [linking, setLinking] = useState(false);
  const debounceRef = useRef(null);

  const handleSearch = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const data = await searchMatters(q, 20);
      setResults(data.cases || data || []);
    } catch { setResults([]); }
    finally { setSearching(false); }
  }, []);

  const onInput = (e) => {
    const v = e.target.value;
    setQuery(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => handleSearch(v), 300);
  };

  const handleLink = async (caseId) => {
    setLinking(true);
    try {
      await linkEmailToCase(emailId, caseId);
      onMatterLinked?.();
    } catch (err) { console.error('Link failed:', err); }
    finally { setLinking(false); }
  };

  if (intelligence?.matched_case_id) {
    const pct = intelligence.match_confidence
      ? Math.round(intelligence.match_confidence * 100) : null;
    return (
      <div className="space-y-2">
        <div className="bg-cyan-600/15 border border-cyan-600/30 rounded-lg px-3 py-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-white truncate">
              Case #{intelligence.matched_case_id}
            </span>
            {pct && (
              <span className="text-[10px] px-1.5 py-0.5 bg-cyan-600/30 text-cyan-300 rounded-full font-medium">
                {pct}% match
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
        <input value={query} onChange={onInput} placeholder="Search matters..."
          className="w-full bg-slate-800 border border-slate-600 rounded-md pl-7 pr-3 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500" />
      </div>
      {searching && (
        <div className="flex items-center justify-center py-2">
          <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
        </div>
      )}
      {results.length > 0 && (
        <div className="max-h-40 overflow-y-auto space-y-1">
          {results.map((c) => (
            <button key={c.id} onClick={() => handleLink(c.id)} disabled={linking}
              className="w-full text-left px-2 py-1.5 rounded text-xs hover:bg-slate-700 transition-colors disabled:opacity-50">
              <div className="text-white truncate">{c.case_name || c.display_number}</div>
              <div className="text-slate-500 truncate">{c.client_name}</div>
            </button>
          ))}
        </div>
      )}
      {query.length >= 2 && !searching && results.length === 0 && (
        <p className="text-xs text-slate-500 text-center py-1">No matters found</p>
      )}
    </div>
  );
};

// ============================================================================
// Panel 2: Deadlines
// ============================================================================

const DeadlinePanel = ({ deadlines, emailId, onDeadlineAccepted }) => {
  const [added, setAdded] = useState({});
  const [adding, setAdding] = useState(null);

  if (!deadlines || deadlines.length === 0) {
    return <p className="text-xs text-slate-500">No deadlines detected</p>;
  }

  const handleAdd = async (dl, idx) => {
    setAdding(idx);
    try {
      const token = localStorage.getItem('token');
      await mailApi.post('/api/ai-calendar/suggestions', {
        source_type: 'email',
        source_id: String(emailId),
        suggested_title: dl.description || 'Deadline',
        suggested_start: dl.date,
        suggested_category: 'deadline',
        confidence_score: dl.confidence || 0.7
      });
      setAdded(prev => ({ ...prev, [idx]: true }));
      onDeadlineAccepted?.();
    } catch (err) { console.error('Add deadline failed:', err); }
    finally { setAdding(null); }
  };

  const fmtDate = (d) => {
    try {
      return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return d; }
  };

  return (
    <div className="space-y-2">
      {deadlines.map((dl, idx) => (
        <div key={idx} className="bg-slate-800/50 rounded-md px-2.5 py-2 space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-amber-300">{fmtDate(dl.date)}</span>
            {dl.confidence && (
              <span className="text-[10px] text-slate-500">{Math.round(dl.confidence * 100)}%</span>
            )}
          </div>
          <p className="text-xs text-slate-300">{dl.description}</p>
          {added[idx] ? (
            <div className="flex items-center gap-1 text-green-400 text-[11px]">
              <Check className="w-3 h-3" /> Added
            </div>
          ) : (
            <button onClick={() => handleAdd(dl, idx)} disabled={adding === idx}
              className="flex items-center gap-1 text-[11px] text-cyan-400 hover:text-cyan-300 disabled:opacity-50">
              {adding === idx ? <Loader2 className="w-3 h-3 animate-spin" /> : <Clock className="w-3 h-3" />}
              Add to Calendar
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// Panel 3: Parties
// ============================================================================

const PartiesPanel = ({ people }) => {
  const [contactResults, setContactResults] = useState({});
  const [searching, setSearching] = useState(null);

  if (!people || people.length === 0) {
    return <p className="text-xs text-slate-500">No parties detected</p>;
  }

  const findContact = async (name, idx) => {
    setSearching(idx);
    try {
      const response = await mailApi.get('/api/contacts', {
        params: { search: name, limit: 5 }
      });
      setContactResults(prev => ({ ...prev, [idx]: response.data?.contacts || response.data || [] }));
    } catch { setContactResults(prev => ({ ...prev, [idx]: [] })); }
    finally { setSearching(null); }
  };

  return (
    <div className="space-y-2">
      {people.map((person, idx) => {
        const name = typeof person === 'string' ? person : person.name || person;
        return (
          <div key={idx} className="space-y-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <User className="w-3 h-3 text-slate-500" />
                <span className="text-xs text-white">{name}</span>
              </div>
              <button onClick={() => findContact(name, idx)} disabled={searching === idx}
                className="text-[10px] text-cyan-400 hover:text-cyan-300 disabled:opacity-50">
                {searching === idx ? '...' : 'Find'}
              </button>
            </div>
            {contactResults[idx] !== undefined && (
              <div className="ml-4">
                {contactResults[idx].length > 0 ? (
                  contactResults[idx].map((c, ci) => (
                    <div key={ci} className="text-[11px] text-slate-400 truncate">
                      {c.first_name} {c.last_name} — {c.email || c.primary_phone_number || ''}
                    </div>
                  ))
                ) : (
                  <span className="text-[11px] text-slate-600">No contacts found</span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ============================================================================
// Panel 4: Amounts
// ============================================================================

const AmountsPanel = ({ amounts }) => {
  if (!amounts || amounts.length === 0) return null; // hide entirely

  return (
    <div className="space-y-1.5">
      {amounts.map((a, idx) => (
        <div key={idx} className="bg-slate-800/50 rounded-md px-2.5 py-1.5">
          <span className="text-xs font-medium text-emerald-400">
            ${typeof a === 'string' ? a : a.amount || a}
          </span>
          {a.context && (
            <p className="text-[11px] text-slate-500 truncate mt-0.5">{a.context}</p>
          )}
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// Panel 5: Documents
// ============================================================================

const DocumentsPanel = ({ documents, matchedCaseId }) => {
  const [docResults, setDocResults] = useState({});
  const [searching, setSearching] = useState(null);

  if (!documents || documents.length === 0) {
    return <p className="text-xs text-slate-500">No document references detected</p>;
  }

  const findDoc = async (docName, idx) => {
    if (!matchedCaseId) return;
    setSearching(idx);
    try {
      const response = await mailApi.get('/api/documents', {
        params: { case_id: matchedCaseId, search: docName, limit: 5 }
      });
      setDocResults(prev => ({ ...prev, [idx]: response.data?.documents || response.data || [] }));
    } catch { setDocResults(prev => ({ ...prev, [idx]: [] })); }
    finally { setSearching(null); }
  };

  return (
    <div className="space-y-2">
      {documents.map((doc, idx) => {
        const name = typeof doc === 'string' ? doc : doc.name || doc;
        return (
          <div key={idx} className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-xs text-white truncate flex-1">{name}</span>
              {matchedCaseId ? (
                <button onClick={() => findDoc(name, idx)} disabled={searching === idx}
                  className="text-[10px] text-cyan-400 hover:text-cyan-300 disabled:opacity-50 flex-shrink-0 ml-2">
                  {searching === idx ? '...' : 'Find'}
                </button>
              ) : (
                <span className="text-[10px] text-slate-600 flex-shrink-0 ml-2" title="Link a matter first">—</span>
              )}
            </div>
            {docResults[idx] !== undefined && (
              <div className="ml-4">
                {docResults[idx].length > 0 ? (
                  docResults[idx].map((d, di) => (
                    <div key={di} className="text-[11px] text-slate-400 truncate">
                      {d.filename || d.name || d.title}
                    </div>
                  ))
                ) : (
                  <span className="text-[11px] text-slate-600">Not found in matter files</span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ============================================================================
// Main Sidebar
// ============================================================================

const IntelligenceSidebar = ({ emailIntelligence, emailId, onMatterLinked, onDeadlineAccepted }) => {
  const [expanded, setExpanded] = useState(() => getStored(SIDEBAR_KEY, true));

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    setStored(SIDEBAR_KEY, next);
  };

  if (!emailIntelligence) return null;

  const intel = emailIntelligence;
  const hasAmounts = intel.extracted_amounts && intel.extracted_amounts.length > 0;

  // Collapsed state
  if (!expanded) {
    return (
      <div className="w-8 flex-shrink-0 bg-slate-800/50 border-l border-slate-700 flex flex-col items-center cursor-pointer hover:bg-slate-700/50 transition-colors"
        onClick={toggle}>
        <div className="mt-4">
          <ChevronLeft className="w-4 h-4 text-slate-500" />
        </div>
        <div className="mt-4 [writing-mode:vertical-lr] text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
          Intelligence
        </div>
      </div>
    );
  }

  // Expanded state
  return (
    <div className="w-[280px] flex-shrink-0 bg-slate-800/30 border-l border-slate-700 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-700">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Intelligence</span>
        <button onClick={toggle} className="p-1 hover:bg-slate-700 rounded transition-colors">
          <ChevronRight className="w-3.5 h-3.5 text-slate-500" />
        </button>
      </div>

      {/* Panels */}
      <div className="flex-1 overflow-y-auto">
        <Panel name="matter" icon={Briefcase} title="Matter">
          <MatterPanel intelligence={intel} emailId={emailId} onMatterLinked={onMatterLinked} />
        </Panel>

        <Panel name="deadlines" icon={Clock} title="Deadlines"
          count={intel.extracted_deadlines?.length}>
          <DeadlinePanel deadlines={intel.extracted_deadlines} emailId={emailId} onDeadlineAccepted={onDeadlineAccepted} />
        </Panel>

        <Panel name="parties" icon={Users} title="Parties"
          count={intel.mentioned_people?.length}>
          <PartiesPanel people={intel.mentioned_people} />
        </Panel>

        {hasAmounts && (
          <Panel name="amounts" icon={DollarSign} title="Amounts"
            count={intel.extracted_amounts?.length}>
            <AmountsPanel amounts={intel.extracted_amounts} />
          </Panel>
        )}

        <Panel name="documents" icon={FileText} title="Documents"
          count={intel.mentioned_documents?.length}>
          <DocumentsPanel documents={intel.mentioned_documents} matchedCaseId={intel.matched_case_id} />
        </Panel>
      </div>
    </div>
  );
};

export default IntelligenceSidebar;
